/**
 * Browser-safe currency conversion shared by every price display, sort and
 * comparison on the site. EUR is the reference currency; `rates[C]` is units
 * of C per 1 EUR. Live rates come from the NexKey API (Frankfurter/ECB,
 * refreshed daily); DEFAULT_RATES are only used until they load.
 * Retailer prices keep their original currency — conversion is view-only.
 */
export type FxRates = Record<string, number>;

export const DEFAULT_RATES: FxRates = {
  EUR: 1, USD: 1.08, GBP: 0.85, PLN: 4.32, CHF: 0.95, CAD: 1.47, AUD: 1.63, JPY: 168,
};

let activeRates: FxRates = { ...DEFAULT_RATES };

export function getActiveRates(): FxRates {
  return activeRates;
}

export function mergeRates(incoming: Record<string, unknown> | null | undefined): FxRates {
  const next: FxRates = { ...DEFAULT_RATES };
  for (const [k, v] of Object.entries(incoming ?? {})) {
    if (typeof v === "number" && Number.isFinite(v) && v > 0) next[k.toUpperCase()] = v;
  }
  next.EUR = 1;
  return next;
}

export function setActiveRates(rates: FxRates): void {
  activeRates = rates;
}

/** Convert minor units; null when a currency is unknown. */
export function convertMinor(
  amount: number,
  from: string | null | undefined,
  to: string | null | undefined,
  rates: FxRates = activeRates,
): number | null {
  const f = (from || "EUR").toUpperCase();
  const t = (to || "EUR").toUpperCase();
  if (f === t) return amount;
  const fr = rates[f];
  const tr = rates[t];
  if (!fr || !tr) return null;
  return Math.round((amount / fr) * tr);
}

/** Comparable EUR minor units for sorting; unknown currencies sort last. */
export function comparableEur(
  amount: number | null | undefined,
  currency: string | null | undefined,
  rates: FxRates = activeRates,
): number {
  if (amount == null) return Number.POSITIVE_INFINITY;
  const v = convertMinor(amount, currency, "EUR", rates);
  return v == null ? Number.POSITIVE_INFINITY : v;
}
